import _ from 'lodash';
import React, { useContext, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { DeleteConfirmation, Dropdown, IconButton } from '~/components';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { ReportContext } from './ReportPage';
import { useReports } from './ReportsContext';
import ReportForm from './ReportForm';
import { colors } from '~/styles';

export default function ReportActions({ onSave }) {
  const reportsContext = useReports();
  const reportContext = useContext(ReportContext);
  const report = reportContext.report;
  const { area } = useParams();

  const { workspace } = useWorkspace();
  const location = useLocation();

  const toast = useToast();
  const api = useApi();

  const [dialog, setDialog] = useState(null);

  const confirmation = useConfirmation();
  const history = useHistory();

  const handleCloseDialog = () => {
    setDialog(null);
  };

  const handleSave = async () => {
    let search = location.search;
    if (_.isFunction(onSave)) search = onSave();
    const params = _.toArray(new URLSearchParams(search ?? location.search).entries());
    await api.www.workspaces(workspace.id).reports(report.id).update({ params });
    toast.success('Report saved successfully.');
    reportContext.onRefetch();
  };

  const handleSaveAs = async () => {
    let search = location.search;
    if (_.isFunction(onSave)) search = onSave();
    const params = _.toArray(new URLSearchParams(search).entries());
    setDialog(
      <ReportForm
        title="Save Report As"
        report={{
          name: report.name,
          description: report.description,
          generatorKey: report.generatorKey,
          templateKey: report.templateKey,
          params,
        }}
        search={search}
        onClose={handleCloseDialog}
        onSaved={(data) => {
          handleCloseDialog();
          history.replace(`/app/${workspace.key}/reports/${area}/${data.slug}`);
        }}
      />,
    );
  };

  const handleDelete = async () => {
    await confirmation.prompt((resolve) => {
      return (
        <DeleteConfirmation
          title="Delete report"
          resolve={async (confirm) => {
            if (confirm) {
              await api.www.workspaces(workspace.id).reports(report.id).remove();
              await reportsContext.fetchData();
              toast.success('Report deleted.');
              history.replace(`/app/${workspace.key}/reports/${area}`);
            }
            resolve(true);
          }}>
          Are you sure you want to delete the report "{report.name}"?
        </DeleteConfirmation>
      );
    });
  };

  const handleEdit = async () => {
    let search = location.search;
    if (_.isFunction(onSave)) search = onSave();
    const params = _.toArray(new URLSearchParams(search).entries());
    setDialog(
      <ReportForm
        title="Edit Report"
        report={{
          id: report.id,
          name: report.name,
          description: report.description,
          generatorKey: report.generatorKey,
          templateKey: report.templateKey,
          params,
        }}
        search={search}
        onClose={handleCloseDialog}
        onSaved={async () => {
          await reportsContext.fetchData();
          handleCloseDialog();
        }}
        onDelete={async () => {
          handleDelete();
        }}
      />,
    );
  };

  return (
    <>
      {report.permissions.manage ? (
        <>
          <IconButton icon="trash" onClick={handleDelete} tooltip="Delete" style={{ color: colors.danger }} />

          <IconButton icon="pencil" onClick={handleEdit} tooltip="Edit" />

          <Dropdown align="right">
            {({ setIsOpen }) => (
              <>
                <Dropdown.Trigger>
                  <IconButton icon="floppy-disk" onClick={() => setIsOpen(true)} tooltip="Save" />
                </Dropdown.Trigger>

                <Dropdown.Menu>
                  <Dropdown.Item
                    onClick={() => {
                      setIsOpen(false);
                      handleSave();
                    }}>
                    Save
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => {
                      setIsOpen(false);
                      handleSaveAs();
                    }}>
                    Save as
                  </Dropdown.Item>
                </Dropdown.Menu>
              </>
            )}
          </Dropdown>
        </>
      ) : (
        <IconButton tooltip="Save As" icon="floppy-disk" onClick={handleSaveAs} />
      )}

      {dialog}
    </>
  );
}
